import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Alert, Button, Card, CardHeader, Edit01 } from "@opendate-app/setlist";
import useTabData from "../../EventPage/hooks/useTabData";
import { toastError } from "../../../shared/toastHelper";
import { formatPrice } from "../../../shared/teamCurrencyFormatting";
import { VISIBILITY_LABELS, formatDateTime, isClassEditable } from "../../ClassesShared/classLabels";
import DetailField from "../registrationPage/DetailField";
import CancelClassModal from "../CancelClassModal";
import cancelClassErrorMessage from "../cancelClassErrorMessage";
import "../../ClassesShared/classesShared.css";
import "../classSettings.css";
import ClassesLoading from "../../ClassesShared/ClassesLoading";

function SettingsCard({ title, editHref, children }) {
  const actions = editHref && (
    <Button as="a" variant="link-color" size="sm" icon={Edit01} href={editHref} aria-label={`Edit ${title.toLowerCase()}`}>Edit</Button>
  );
  return (
    <Card header={<CardHeader title={title} actions={actions} />}>
      <dl className="row mb-0">{children}</dl>
    </Card>
  );
}

SettingsCard.propTypes = { title: PropTypes.string.isRequired, editHref: PropTypes.string, children: PropTypes.node };

export default function SettingsTab({ url, cancelPath, editPath, canManage = false, csrfToken, timeZone }) {
  const { loading, data, error } = useTabData(url);
  const [working, setWorking] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  if (loading) return <ClassesLoading />;
  if (error) return <Alert color="error" role="alert" description="Could not load settings." />;
  const ci = data.classInstance || {};
  const published = ci.status === "published";
  const editStep = (step) => (editPath && isClassEditable(ci.status) ? `${editPath}?step=${step}` : null);

  const submitCancel = async (reason) => {
    setWorking(true);
    try {
      await axios.post(cancelPath, { cancellation_reason: reason }, { headers: { "X-CSRF-Token": csrfToken } });
      window.location.reload();
    } catch (e) {
      setWorking(false);
      toastError(cancelClassErrorMessage(e));
    }
  };

  return (
    <div className="class-settings d-flex flex-column classes-gap-24">
      <SettingsCard title="Image" editHref={editStep("details")}>
        <DetailField label="Class image" wide>
          {ci.main_image_url
            ? <img src={ci.main_image_url} alt="Class image" className="classes-image-preview" />
            : "No image. The class page shows a color gradient."}
        </DetailField>
      </SettingsCard>

      <SettingsCard title="Visibility" editHref={editStep("visibility")}>
        <DetailField label="Listing">{VISIBILITY_LABELS[ci.visibility]}</DetailField>
        <DetailField label="Registration opens">
          {ci.registration_opens_at ? formatDateTime(ci.registration_opens_at, timeZone) : "When the class is published"}
        </DetailField>
        <DetailField label="Registration closes">
          {ci.registration_closes_at ? formatDateTime(ci.effective_registration_closes_at, timeZone) : "When the first session starts"}
        </DetailField>
      </SettingsCard>

      <SettingsCard title="Capacity & waitlist" editHref={editStep("pricing")}>
        <DetailField label="Maximum spots">{ci.capacity != null ? `${ci.capacity} (${ci.registered_count ?? 0} registered)` : null}</DetailField>
        <DetailField label="Waitlist">{ci.waitlist_enabled ? `On · ${ci.waitlist_claim_window_label} to claim a freed spot` : "Off"}</DetailField>
        <DetailField label="Refund window">{ci.refund_window_days != null ? `${ci.refund_window_days} days before the first session` : null}</DetailField>
      </SettingsCard>

      <SettingsCard title="Pricing" editHref={editStep("pricing")}>
        <DetailField label="Price per spot">{ci.price != null ? formatPrice(Number(ci.price), data.currencyCode) : null}</DetailField>
        <DetailField label="Group discount">
          {ci.group_discount_threshold ? `${Number(ci.group_discount_percentage)}% off for ${ci.group_discount_threshold} or more` : "None"}
        </DetailField>
      </SettingsCard>

      {canManage && ci.may_cancel && (
        <Card className="class-settings__danger" header={<CardHeader title={<span className="text-danger">Danger zone</span>} />}>
          <div className="d-flex justify-content-between align-items-center flex-wrap classes-gap-16">
            <div>
              <div className="font-weight-bold">Cancel this class</div>
              <div className="classes-text-secondary small">
                {published
                  ? "Cancels every registration, refunds paid attendees and emails them. This can't be undone."
                  : "Canceling this draft class can't be undone."}
              </div>
            </div>
            <div className="classes-danger-outline">
              <Button variant="secondary" onClick={() => setCancelOpen(true)} disabled={working}>Cancel Class</Button>
            </div>
          </div>
        </Card>
      )}
      {cancelOpen && (
        <CancelClassModal
          classLabel={ci.name}
          registrationCount={ci.registered_count ?? 0}
          paidCount={data.paidRegistrations}
          requireReason={published}
          working={working}
          onClose={() => setCancelOpen(false)}
          onConfirm={submitCancel}
        />
      )}
    </div>
  );
}

SettingsTab.propTypes = { url: PropTypes.string.isRequired, cancelPath: PropTypes.string.isRequired, editPath: PropTypes.string, canManage: PropTypes.bool, csrfToken: PropTypes.string.isRequired, timeZone: PropTypes.string };
